import React from 'react'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import type { DisplayMode } from '@/services/clinicProgramService'

interface ConfirmDisplayModeChangeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  programTitle: string
  fromMode: DisplayMode
  toMode: DisplayMode
  onConfirm: () => Promise<void> | void
  isLoading?: boolean
}

const modeLabel = (mode: DisplayMode): string =>
  mode === 'protocol_showcase' ? 'Nimbus Routed' : 'In-House'

const ConfirmDisplayModeChangeDialog: React.FC<ConfirmDisplayModeChangeDialogProps> = ({
  open,
  onOpenChange,
  programTitle,
  fromMode,
  toMode,
  onConfirm,
  isLoading = false,
}) => {
  const handleConfirm = async () => {
    await onConfirm()
    onOpenChange(false)
  }

  return (
    <AlertDialog open={open} onOpenChange={(v) => { if (!isLoading) onOpenChange(v) }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Change Program Routing</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className='space-y-2 text-sm text-muted-foreground'>
              <p>
                You are about to switch <span className='font-semibold text-slate-900'>"{programTitle}"</span> from{' '}
                <span className='font-semibold text-slate-900'>{modeLabel(fromMode)}</span> to{' '}
                <span className='font-semibold text-slate-900'>{modeLabel(toMode)}</span>.
              </p>
              <p>
                This will change the payment flow and prescription handling for all future orders on this program.
              </p>
              <p>Are you sure you want to continue?</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          <Button
            onClick={handleConfirm}
            disabled={isLoading}
            className='bg-amber-600 hover:bg-amber-700 text-white'
          >
            {isLoading ? 'Switching...' : 'Confirm'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default ConfirmDisplayModeChangeDialog
